import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Star, Clock, Calendar, Trophy, User, MapPin, Boxes } from "lucide-react";
import LocationInfo from "./LocationInfo";

// Move this function below imports but above the component
export const calculateAccountAge = (startDate) => {
  if (!startDate) return 0;
  const start = new Date(startDate);
  const now = new Date();
  const diffTime = Math.abs(now - start);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const AccountDetails = ({ player }) => {
  // Convert Unix timestamp to MM/DD/YY
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });
  };

  // Format VIP expiration with time
  const formatVIPExpiration = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Count completed quests
  const countCompletedQuests = (quests) => {
    if (!quests || !Array.isArray(quests)) return 0;
    return quests.filter((quest) => quest.isCompleted).length;
  };

  // VIP status based purely on presence of vip object
  const hasVIP = !!player?.vip;
  const vipTier = player?.vip?.count;
  const vipExpiration = player?.vip?.expiration;

  const accountAge = calculateAccountAge(player?.createdAt);
  const completedQuests = countCompletedQuests(player?.quests);
  const avatar = player?.currentAvatar;
  const avatarID = avatar?.pieces?.tokenId || avatar?.tokenId;
  const avatarName = avatar?.id
    ? avatarID
      ? `${avatar.id} #${avatarID}`
      : avatar.id
    : "Default Avatar";
  const faction = player?.faction === "1" ? "Wildgroves" : player?.faction === "2" ? "Seedwrights" : "Reapers";

  return (
    <Card className=" w-full bg-[var(--panel-section-bg)] bg-opacity-80 text-[var(--text-color)]  rounded-2xl shadow-lg border border-[var(--border-color)]  transition-transform duration-300 hover:shadow-[0px_0px_10px_2px] hover:shadow-[var(--border-color)] ">
      <CardHeader>
        <CardTitle className="text-lg font-bold text-center">Account Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Location Info - Fixed alignment */}
        <div className="flex items-center justify-between bg-[var(--panel-sections)] p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <MapPin className="text-[var(--icons)] " size={20} />
            <span className="text-sm font-medium">Location</span>
          </div>
          <LocationInfo playerData={player} />
        </div>
        {/* VIP Status */}
        <div className="flex items-center justify-between bg-[var(--panel-sections)] p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <Star className={`${hasVIP ? "text-yellow-500" : "text-[var(--icons)] "}`} size={20} />
            <span className="text-sm font-medium">VIP Status</span>
          </div>
          <span className="text-sm">
            {hasVIP ? (
              <div className="text-right">
                <div className="font-semibold text-yellow-600">Active VIP (Tier {vipTier})</div>
                <div className="text-xs text-[var(--text-color)] ">Expires: {formatVIPExpiration(vipExpiration)}</div>
              </div>
            ) : (
              "No VIP"
            )}
          </span>
        </div>
        {/* Account Age */}
        <div className="flex items-center justify-between bg-[var(--panel-sections)]  p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <Clock className="text-[var(--icons)] " size={20} />
            <span className="text-sm font-medium">Account Age</span>
          </div>
          <span className="text-sm">{accountAge} days</span>
        </div>
        {/* Start Date */}
        <div className="flex items-center justify-between bg-[var(--panel-sections)]  p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <Calendar className="text-[var(--icons)] " size={20} />
            <span className="text-sm font-medium">Start Date</span>
          </div>
          <span className="text-sm">{formatDate(player?.createdAt)}</span>
        </div>
        {/* Harvest Union */}
        <div className="flex items-center justify-between bg-[var(--panel-sections)]  p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <Boxes className="text-[var(--icons)] " size={20} />
            <span className="text-sm font-medium">Harvest Union</span>
          </div>
          <span className="text-sm">{faction}</span>
        </div>
        {/* Completed Quests */}
        <div className="flex items-center justify-between bg-[var(--panel-sections)]  p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <Trophy className="text-[var(--icons)] " size={20} />
            <span className="text-sm font-medium">Completed Quests</span>
          </div>
          <span className="text-sm">{completedQuests}</span>
        </div>
        {/* Current Avatar */}
        <div className="flex items-center justify-between bg-[var(--panel-sections)]  p-3 rounded-lg">
          <div className="flex items-center gap-2">
            <User className="text-[var(--icons)] " size={20} />
            <span className="text-sm font-medium">Current Avatar</span>
          </div>
          <span className="text-sm truncate max-w-[150px]" title={avatarName}>
            {avatarName}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default AccountDetails;
